import * as React from 'react';
import {
  getIntrinsicElementProps,
  slot,
  Button,
  useArrowNavigationGroup,
  mergeCallbacks,
  useFluent,
  useMergedRefs,
} from '@fluentui/react-components';
import type { PromptStarterListProps, PromptStarterListState } from './PromptStarterList.types';
import { useDesignVersion } from '@fluentui-copilot/react-provider';
import { ChevronDown12Regular } from '@fluentui/react-icons';
import { PromptStarterMotion } from '../PromptStarter/promptStarterMotion';
import { useControllableState } from '@fluentui/react-utilities';

/**
 * Create the state required to render PromptStarterList.
 *
 * The returned state can be modified with hooks such as usePromptStarterListStyles_unstable,
 * before being passed to renderPromptStarterList_unstable.
 *
 * @param props - props from this instance of PromptStarterList
 * @param ref - reference to root HTMLElement of PromptStarterList
 */
export const usePromptStarterList_unstable = (
  props: PromptStarterListProps,
  ref: React.Ref<HTMLDivElement>,
): PromptStarterListState => {
  const {
    arrowNavigationOptions,
    expandButtonLabel = 'Show more',
    collapseButtonLabel = 'Show less',
    onExpandedChange,
    animateOnMount = true,
    visibleRows = 1,
  } = props;
  const designVersion = useDesignVersion(props.designVersion);
  const focusAttrs = useArrowNavigationGroup({
    ...arrowNavigationOptions,
    axis: arrowNavigationOptions?.axis ?? 'both',
    memorizeCurrent: arrowNavigationOptions?.memorizeCurrent ?? true,
  });

  const [isExpanded, setIsExpanded] = useControllableState({
    state: props.isExpanded,
    defaultState: props.defaultExpanded,
    initialState: false,
  });
  const toggleExpanded = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setIsExpanded(prevExpanded => {
        onExpandedChange?.(event, { isExpanded: !prevExpanded });
        return !prevExpanded;
      });
    },
    [setIsExpanded, onExpandedChange],
  );
  const [numberOfColumns, setNumberOfColumns] = React.useState<number>(3);

  const gridWrapperRef = React.useRef<HTMLDivElement>(null);
  const { targetDocument } = useFluent();
  const win = targetDocument?.defaultView;

  React.useEffect(() => {
    if (!win || !gridWrapperRef.current) return;

    const promptStarterGrid = gridWrapperRef.current;

    const updateNumberOfColumns = () => {
      const numColumns = win.getComputedStyle(promptStarterGrid).gridTemplateColumns.split(' ').length;
      setNumberOfColumns(numColumns);
    };

    const ro = new win.ResizeObserver(updateNumberOfColumns);
    ro.observe(promptStarterGrid);

    return () => {
      ro.unobserve(promptStarterGrid);
    };
  }, [win]);

  const state: PromptStarterListState = {
    numberOfColumns: numberOfColumns,
    isExpanded: isExpanded,
    designVersion,
    components: {
      root: 'div',
      gridWrapper: 'div',
      actions: 'span',
      expandButton: Button,
    },
    root: slot.always(
      getIntrinsicElementProps('div', {
        ref,
        ...props,
      }),
      { elementType: 'div' },
    ),
    gridWrapper: slot.always(props.gridWrapper, {
      defaultProps: {
        role: 'list',
        ...focusAttrs,
      },
      elementType: 'div',
    }),
    actions: slot.optional(props.actions, {
      elementType: 'span',
    }),
    expandButton: slot.optional(props.expandButton, {
      defaultProps: {
        appearance: 'subtle',
        size: 'small',
        shape: 'rounded',
        icon: <ChevronDown12Regular />,
        iconPosition: 'after',
        'aria-expanded': isExpanded,
        children: isExpanded ? collapseButtonLabel : expandButtonLabel,
      },
      renderByDefault: true,
      elementType: Button,
    }),
  };

  state.gridWrapper.ref = useMergedRefs(gridWrapperRef, state.gridWrapper.ref);

  // Get the array of children.
  const childrenArray = React.useMemo<
    (React.ReactElement<any> | number | string | Iterable<React.ReactNode> | React.ReactPortal)[]
  >(() => {
    return React.Children.toArray(state.root.children);
  }, [state.root.children]);

  // Add motion to the children.
  const childrenWithMotion = React.useMemo(
    () =>
      childrenArray.map((child, index) => {
        // Throw error if the child is not a valid React element or is a Fragment.
        if (!React.isValidElement(child) || child.type === React.Fragment) {
          throw new Error('You should only use PromptStarter components as the children of PromptStarterList.');
        }

        // In the collapsed state, if there is one column, we show three children. If there are two or three columns,
        // we show two or three (or four or six, if there are two visible rows). All children are shown in the isExpanded state.
        const visibility = (numberOfColumns === 1 && index < 3) || index < numberOfColumns * visibleRows || isExpanded;

        return (
          <PromptStarterMotion
            appear={animateOnMount}
            key={index}
            index={index}
            visible={visibility}
            numberOfColumns={numberOfColumns}
          >
            {child}
          </PromptStarterMotion>
        );
      }),
    [childrenArray, numberOfColumns, isExpanded],
  );

  state.root.children = childrenWithMotion;

  const numChildren = childrenArray.length;
  if ((numberOfColumns === 1 && numChildren <= 3) || numChildren <= numberOfColumns * visibleRows) {
    state.expandButton = undefined;
  }

  if (state.expandButton) {
    state.expandButton.onClick = mergeCallbacks(
      state.expandButton.onClick as React.MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>,
      toggleExpanded,
    );
  }

  return state;
};
